import { useApolloClient } from "@apollo/client";
import { ListItem, useDebouncedValue, useScreenWidth } from "@msys/ui";
import {
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import {
  Chip,
  Divider,
  Icon,
  IconButton,
  Link as MuiLink,
  Tooltip,
  Typography,
} from "@mui/material";
import { Circle, GoogleMap, InfoWindow, MarkerF } from "@react-google-maps/api";
import { useTranslate } from "@tolgee/react";
import { values } from "lodash-es";
import React, {
  forwardRef,
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useLatest, useUpdateEffect } from "react-use";
import { useGoogleMapsApi } from "../../google-maps/useGoogleMapsApi.js";
import { color } from "../../../common/MuiThemeProvider.js";
import EditCalendarIcon from "../../assets/icons/edit-calendar.svg?react";
import companyLocationIconUrl from "../../assets/icons/icon-map-company.svg";
import craftsmanLocationIconUrl from "../../assets/icons/icon-map-craftsman.svg";
import pinIconBlueUrl from "../../assets/icons/icon-map-pin-blue.svg";
import pinIconRedUrl from "../../assets/icons/icon-map-pin-red.svg";
import { RestrictedByProjectPermissionWithDebug } from "../../auth/RestrictedByProjectPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { useLatestDefinedValue } from "../../commons/hooks/useLatestDefinedValue.js";
import { Stack } from "../../commons/layout/Stack.js";
import { AddressDetails__AddressFragment } from "../addresses/Addresses.generated.js";
import { getAddressLabel, getAddressSearchUrl } from "../addresses/helpers.js";
import { usePhoneTypes } from "../phones/usePhoneTypes.js";
import { ProjectListItem } from "../projects/components/ProjectListItem.js";
import { useOrganisationRoles } from "../users/useRoles.js";
import {
  PlanningMapProjectFragment,
  PlanningMapResourceFragment,
  usePlanningMapQuery,
} from "./PlanningMap.generated.js";

// Hackescher Markt
const defaultCenter = {
  lat: 52.523502,
  lng: 13.401785,
};
const defaultBounds = {
  northeast: { lat: defaultCenter.lat + 0.1, lng: defaultCenter.lng + 0.1 },
  southwest: { lat: defaultCenter.lat - 0.1, lng: defaultCenter.lng - 0.1 },
};

const CONTAINER_STYLE = {
  maxWidth: "1280px",
  margin: "0 auto",
  width: "100%",
  height: "100%",
  flexGrow: 1,
  flexShrink: 1,
};

const LIMIT_USERS_ON_MAP = 50;

const CIRCLE_OPTIONS = {
  fillColor: color.secondary,
  fillOpacity: 0.2,
  strokeColor: color.secondary,
  strokeOpacity: 0.7,
};

type PlanningMapBuilding = PlanningMapProjectFragment["buildingInfo"] & {
  projects: PlanningMapProjectFragment[];
};
type PlanningMapResource = {
  address: AddressDetails__AddressFragment & {
    location: NonNullable<AddressDetails__AddressFragment["location"]>;
  };
  craftsmen: PlanningMapResourceFragment[];
};

export interface PlanningMapRef {
  setSelectedBuildingId: (buildingId?: string) => void;
  setSelectedResourceId: (resourceId?: string) => void;
}

interface Props {
  projects: PlanningMapProjectFragment[];
  maxWidth?: number;
  fitBounds?: boolean;
  initialZoom?: number;
  initialCenter?: { lat: number; lng: number };
  initialSelectedBuildingId?: string;
  initialSelectedResourceId?: string;
  selectedProjectAddress?: AddressDetails__AddressFragment;
  resourceMaxDistance?: number;
  RightButtons?: React.ReactNode;
}

export const PlanningMap = forwardRef<PlanningMapRef, Props>(
  (
    {
      projects,
      maxWidth,
      fitBounds = true,
      initialZoom = 4,
      initialCenter,
      initialSelectedBuildingId,
      initialSelectedResourceId,
      selectedProjectAddress,
      resourceMaxDistance,
      RightButtons,
    },
    ref
  ) => {
    const { t } = useTranslate(["PlanningModal"]);
    const { isLoaded } = useGoogleMapsApi();
    const viewer = useUserData().currentUser!;
    const organisationAddress =
      viewer.organisation.branchAddress ?? viewer.organisation.billingAddress;

    const [map, setMap] = React.useState<google.maps.Map | null>(null);
    const mapLatest = useLatest(map);

    const [center, setCenter] = useState<{ lat: number; lng: number }>(
      initialCenter ??
        (organisationAddress?.location
          ? {
              lat: organisationAddress.location.lat,
              lng: organisationAddress.location.lng,
            }
          : defaultCenter)
    );
    const [zoom, setZoom] = useState<number>(initialZoom);

    const [lastCoords, setLastCoords] = useState<{
      center: { lat: number; lng: number } | null;
      bounds: {
        northeast: { lat: number; lng: number };
        southwest: { lat: number; lng: number };
      } | null;
    }>({ center: null, bounds: null });
    const lastCoordsLatest = useLatest(lastCoords);
    const lastCoordsDebounced = useDebouncedValue(lastCoords, 1000, {
      leading: false,
      trailing: true,
    });
    const client = useApolloClient();
    const query = usePlanningMapQuery({
      client,
      variables: {
        center: lastCoordsDebounced.center || defaultCenter,
        bounds: lastCoordsDebounced.bounds || defaultBounds,
        limit: LIMIT_USERS_ON_MAP + 1,
      },
      skip: !lastCoordsDebounced.center || !lastCoordsDebounced.bounds,
      fetchPolicy: "cache-and-network",
    });

    const coordsUpdate = React.useRef(() => {
      if (!mapLatest.current) return;
      const mapCenter = mapLatest.current.getCenter();
      const mapBounds = mapLatest.current.getBounds();
      if (!mapCenter || !mapBounds) return;

      const newCenter = { lat: mapCenter.lat(), lng: mapCenter.lng() };
      const newBounds = {
        northeast: {
          lat: mapBounds.getNorthEast().lat(),
          lng: mapBounds.getNorthEast().lng(),
        },
        southwest: {
          lat: mapBounds.getSouthWest().lat(),
          lng: mapBounds.getSouthWest().lng(),
        },
      };

      if (
        newCenter.lat !== lastCoordsLatest.current.center?.lat ||
        newCenter.lng !== lastCoordsLatest.current.center?.lng ||
        newBounds.northeast.lat !==
          lastCoordsLatest.current.bounds?.northeast.lat ||
        newBounds.northeast.lng !==
          lastCoordsLatest.current.bounds?.northeast.lng ||
        newBounds.southwest.lat !==
          lastCoordsLatest.current.bounds?.southwest.lat ||
        newBounds.southwest.lng !==
          lastCoordsLatest.current.bounds?.southwest.lng
      ) {
        setLastCoords({ center: newCenter, bounds: newBounds });
      }
    });

    const resources = useLatestDefinedValue(
      React.useMemo(
        () => query.data?.organisationMemberships.edges.map(edge => edge.node),
        [query.data?.organisationMemberships.edges]
      )
    );

    useUpdateEffect(() => {
      const newCenter =
        initialCenter ??
        (organisationAddress?.location
          ? {
              lat: organisationAddress.location.lat,
              lng: organisationAddress.location.lng,
            }
          : undefined);
      if (newCenter) setCenter(newCenter);
    }, [initialCenter, organisationAddress]);

    useUpdateEffect(() => {
      if (initialZoom) setZoom(initialZoom);
    }, [initialZoom]);

    const [selectedBuildingId, setSelectedBuildingId] = useState<
      string | undefined
    >(initialSelectedBuildingId);

    const [selectedResourceId, setSelectedResourceId] = useState<
      string | undefined
    >(initialSelectedResourceId);

    const onLoad = React.useCallback((map: google.maps.Map) => {
      setMap(map);
    }, []);

    React.useImperativeHandle(ref, () => ({
      setSelectedBuildingId,
      setSelectedResourceId,
    }));

    const mapBuildings: PlanningMapBuilding[] = useMemo(() => {
      const buildings: Record<string, PlanningMapBuilding> = {};

      projects.forEach(project => {
        if (
          !project?.buildingInfo?.id ||
          // we only need buildings with location here!
          !project?.buildingInfo.buildingAddress?.location
        )
          return;

        if (!buildings[project.buildingInfo.id]) {
          buildings[project.buildingInfo.id] = {
            ...project.buildingInfo,
            projects: [],
          };
        }

        buildings[project.buildingInfo.id].projects.push(project);
      });

      return values(buildings) as PlanningMapBuilding[];
    }, [projects]);

    const mapResources: PlanningMapResource[] = useMemo(() => {
      const craftsmen: Record<string, PlanningMapResource> = {};
      (resources ?? [])
        .flatMap(r =>
          r.homeAddress && r.homeAddress.location
            ? [
                {
                  resource: r,
                  address: r.homeAddress,
                  location: r.homeAddress.location,
                },
              ]
            : []
        )
        .forEach(resource => {
          const key = `${resource.location.lat}-${resource.location.lng}`;
          craftsmen[key] = craftsmen[key] || {
            address: resource.address,
            craftsmen: [],
          };
          craftsmen[key].craftsmen.push(resource.resource);
        });

      return values(craftsmen) as PlanningMapResource[];
    }, [resources]);

    const selectedBuilding: PlanningMapBuilding | undefined = useMemo(
      () =>
        selectedBuildingId
          ? mapBuildings.find(b => b.id === selectedBuildingId)
          : undefined,
      [mapBuildings, selectedBuildingId]
    );

    const selectedResource: PlanningMapResource | undefined = useMemo(
      () =>
        selectedResourceId
          ? mapResources.find(r =>
              r.craftsmen.some(c => c.id === selectedResourceId)
            )
          : undefined,
      [mapResources, selectedResourceId]
    );

    useEffect(() => {
      if (!map) return;
      if (!fitBounds) return;

      const bounds = new google.maps.LatLngBounds();

      mapBuildings.forEach(building => {
        if (building.buildingAddress?.location) {
          bounds.extend(building.buildingAddress.location);
        }
      });

      if (organisationAddress && organisationAddress.location) {
        bounds.extend(organisationAddress.location);
      }

      // Don't zoom in too far on only one marker
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        const extendPoint1 = new google.maps.LatLng(
          bounds.getNorthEast().lat() + 0.1,
          bounds.getNorthEast().lng() + 0.1
        );
        const extendPoint2 = new google.maps.LatLng(
          bounds.getNorthEast().lat() - 0.1,
          bounds.getNorthEast().lng() - 0.1
        );
        bounds.extend(extendPoint1);
        bounds.extend(extendPoint2);
      }

      map.fitBounds(bounds);

      const zoom = map.getZoom();
      // show only area around organisation if zoomed out to far
      if (
        zoom &&
        zoom < 6 &&
        organisationAddress &&
        organisationAddress.location
      ) {
        map.setCenter(organisationAddress.location);
        map.setZoom(6);
      }
    }, [map, mapBuildings, organisationAddress, fitBounds]);

    if (!isLoaded) return null;

    return (
      <Stack
        flexDirection="column"
        flex={1}
        width="100%"
        height="100%"
        spacing={1}
        overflow="hidden"
        position="relative"
      >
        {RightButtons && (
          <Stack justifyContent="flex-end" alignItems="center">
            {RightButtons}
          </Stack>
        )}
        {/*@ts-ignore property 'children' does not exist*/}
        <GoogleMap
          options={{ mapTypeControl: false, streetViewControl: false }}
          mapTypeId={google.maps.MapTypeId.ROADMAP}
          mapContainerStyle={{
            ...CONTAINER_STYLE,
            ...(maxWidth ? { maxWidth } : undefined),
          }}
          onLoad={map => {
            onLoad(map);
            setTimeout(coordsUpdate.current);
          }}
          zoom={zoom}
          center={center}
          onCenterChanged={coordsUpdate.current}
          onBoundsChanged={coordsUpdate.current}
          onZoomChanged={coordsUpdate.current}
        >
          <OrganisationMarker />

          {mapResources.map(resource => (
            <MarkerF
              visible
              key={
                // TODO better use some object hash function
                resource.address.streetLines1 +
                resource.address.postalCode +
                resource.address.city
              }
              label={
                resource.craftsmen.length > 1
                  ? {
                      text: resource.craftsmen.length.toString(),
                      className: "msys-map-pin-label",
                      fontSize: "12px",
                    }
                  : undefined
              }
              position={{
                lat: resource.address.location.lat,
                lng: resource.address.location.lng,
              }}
              onClick={() => setSelectedResourceId(resource.craftsmen[0].id)}
              icon={getCraftsmanMarker()}
              zIndex={1}
            />
          ))}
          {mapBuildings
            .flatMap(building =>
              building.buildingAddress?.location
                ? {
                    building,
                    location: building.buildingAddress.location,
                  }
                : []
            )
            .map(({ building, location }) => (
              <MarkerF
                visible
                key={building.id}
                label={
                  building.projects.length > 1
                    ? {
                        text: building.projects.length.toString(),
                        className: "msys-map-pin-label",
                        fontSize: "12px",
                      }
                    : undefined
                }
                position={{
                  lat: location.lat,
                  lng: location.lng,
                }}
                onClick={() => setSelectedBuildingId(building.id)}
                icon={
                  building.projects.find(project => !project.planned)
                    ? getPinMarker("red")
                    : getPinMarker("blue")
                }
                zIndex={2}
              />
            ))}
          {selectedResource && (
            // @ts-ignore property 'children' does not exist
            <InfoWindow
              options={{ pixelOffset: new google.maps.Size(0, -8) }}
              position={{
                lat: selectedResource.address.location.lat,
                lng: selectedResource.address.location.lng,
              }}
              onCloseClick={() => setSelectedResourceId(undefined)}
              zIndex={998}
            >
              <ResourceInfoWindow resource={selectedResource} />
            </InfoWindow>
          )}
          {selectedBuilding && selectedBuilding.buildingAddress?.location && (
            // @ts-ignore property 'children' does not exist
            <InfoWindow
              options={{ pixelOffset: new google.maps.Size(0, -20) }}
              position={{
                lat: selectedBuilding.buildingAddress.location.lat,
                lng: selectedBuilding.buildingAddress.location.lng,
              }}
              onCloseClick={() => setSelectedBuildingId(undefined)}
              zIndex={999}
            >
              <BuildingInfoWindow building={selectedBuilding} />
            </InfoWindow>
          )}
          {selectedProjectAddress &&
            selectedProjectAddress.location &&
            (resourceMaxDistance ?? 0) > 0 && (
              <Circle
                options={CIRCLE_OPTIONS}
                center={{
                  lat: selectedProjectAddress.location.lat,
                  lng: selectedProjectAddress.location.lng,
                }}
                radius={resourceMaxDistance! * 1000}
                draggable={false}
                editable={false}
              />
            )}
        </GoogleMap>
        {mapResources.length > LIMIT_USERS_ON_MAP && (
          <Chip
            sx={{
              position: "absolute",
              mb: 2,
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
            }}
            variant="filled"
            color="secondary"
            label={t(
              "Only first {count, plural, one {craftsman} other {# craftsmen}} are shown. To see more, zoom in the map",
              { ns: "PlanningModal", count: LIMIT_USERS_ON_MAP }
            )}
          />
        )}
      </Stack>
    );
  }
);

function OrganisationMarker() {
  const viewer = useUserData().currentUser!;

  const [isOpen, setIsOpen] = React.useState(false);

  const address =
    viewer.organisation.branchAddress ?? viewer.organisation.billingAddress;
  if (!address) return null;

  if (!address.location) return null;

  return (
    // @ts-ignore property 'children' does not exist
    <MarkerF
      visible
      position={{
        lat: address.location.lat,
        lng: address.location.lng,
      }}
      icon={getCompanyMarker()}
      onClick={() => setIsOpen(true)}
    >
      {isOpen && (
        // @ts-ignore property 'children' does not exist
        <InfoWindow
          options={{ pixelOffset: new google.maps.Size(0, -8) }}
          position={{
            lat: address.location.lat,
            lng: address.location.lng,
          }}
          onCloseClick={() => setIsOpen(false)}
          zIndex={997}
        >
          <Stack flexDirection="column" spacing={0}>
            <MuiLink
              href={`/organisation/profile`}
              target="_blank"
              rel="nofollow noreferrer"
            >
              <Typography variant="h3">{viewer.organisation.title}</Typography>
            </MuiLink>
            {address && (
              <MuiLink
                href={getAddressSearchUrl(address)}
                target="_blank"
                rel="nofollow noreferrer"
              >
                <Typography variant="body2">
                  {getAddressLabel(address, ", ")}
                </Typography>
              </MuiLink>
            )}
          </Stack>
        </InfoWindow>
      )}
    </MarkerF>
  );
}

const ResourceInfoWindow = ({
  resource,
}: {
  resource: PlanningMapResource;
}) => {
  const { phoneTypeLabels } = usePhoneTypes();
  const { getOrganisationRoleTitle } = useOrganisationRoles();

  return (
    <Stack flexDirection="column">
      {resource.craftsmen.map(craftsman => (
        <Fragment key={craftsman.id}>
          <Stack flexDirection="column" spacing={0}>
            <MuiLink
              href={`/organisation/users/${craftsman.id}/profile`}
              target="_blank"
              rel="nofollow noreferrer"
            >
              <Typography variant="h3">{craftsman.fullname}</Typography>
            </MuiLink>

            <Typography variant="caption" color="secondary">
              {getOrganisationRoleTitle(craftsman.roles)}
            </Typography>

            {craftsman.email && (
              <Stack spacing={0.5}>
                <EmailIcon
                  fontSize="small"
                  style={{
                    flexGrow: 0,
                    flexShrink: 0,
                    position: "relative",
                    top: 1,
                  }}
                />
                <Typography variant="body2">
                  <MuiLink href={`mailto:${craftsman.email}`}>
                    {craftsman.email}
                  </MuiLink>
                </Typography>
              </Stack>
            )}

            {craftsman.phones.length > 0 &&
              craftsman.phones.map((phone, index) => {
                if (phone.type === "PRIVATE")
                  throw new Error("Invalid phone type");
                return (
                  <Stack key={phone.id} spacing={0.5}>
                    <PhoneIcon
                      fontSize="small"
                      style={{ flexGrow: 0, flexShrink: 0 }}
                    />
                    <div>
                      <Typography variant="body2" key={index}>
                        {phoneTypeLabels[phone.type]}{" "}
                        <MuiLink href={`tel:${phone.number}`}>
                          {phone.number}
                        </MuiLink>
                      </Typography>
                    </div>
                  </Stack>
                );
              })}
          </Stack>
          <Divider />
        </Fragment>
      ))}

      <MuiLink
        href={getAddressSearchUrl(resource.address)}
        target="_blank"
        rel="nofollow noreferrer"
      >
        <Typography variant="body2">
          {getAddressLabel(resource.address, ", ")}
        </Typography>
      </MuiLink>
    </Stack>
  );
};

const BuildingInfoWindow = ({
  building,
}: {
  building: PlanningMapBuilding;
}) => {
  const viewer = useUserData().currentUser!;
  const { t } = useTranslate(["PlanningModal"]);
  const { isMaxPhone } = useScreenWidth();

  return (
    <Stack flexDirection="column">
      {building.buildingAddress && (
        <Stack flexDirection="row" alignItems="center" spacing={0.5}>
          <LocationOnIcon
            fontSize="small"
            style={{ flexGrow: 0, flexShrink: 0 }}
          />
          <Typography variant="body2">
            <MuiLink
              href={`/buildings/${building.id}`}
              target="_blank"
              rel="nofollow noreferrer"
            >
              {getAddressLabel(building.buildingAddress, ", ")}
            </MuiLink>
          </Typography>
        </Stack>
      )}
      <Stack
        flexDirection="column"
        spacing={0}
        width={isMaxPhone ? "300px" : "360px"}
      >
        <Divider />
        {building.projects.map(project => (
          <ListItem
            key={project.id}
            component={Link}
            // @ts-ignore
            to={`/projects/${project.id}`}
          >
            <ProjectListItem
              project={project}
              Action={
                <RestrictedByProjectPermissionWithDebug
                  permission="MANAGE_PROJECT"
                  project={project}
                >
                  {viewer.organisation.id ===
                    project.owningSystemOrganisationId && (
                    <Tooltip
                      title={
                        project.planned
                          ? t("Re-schedule", { ns: "PlanningModal" })
                          : t("Schedule", { ns: "PlanningModal" })
                      }
                    >
                      <IconButton
                        component={Link}
                        size="small"
                        color="primary"
                        rel="noopener noreferrer"
                        target="_blank"
                        to={`/planning/projects/${project.id}`}
                        style={{ flexGrow: 0, flexShrink: 0 }}
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                        }}
                      >
                        <Icon fontSize="small">
                          <EditCalendarIcon
                            style={{ height: 18, width: 18, display: "flex" }}
                          />
                        </Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                </RestrictedByProjectPermissionWithDebug>
              }
            />
          </ListItem>
        ))}
      </Stack>
    </Stack>
  );
};

function getPinMarker(color: "red" | "blue") {
  return {
    url: color === "red" ? pinIconRedUrl : pinIconBlueUrl,
    labelOrigin: new google.maps.Point(2, 2),
  };
}

function getCompanyMarker() {
  return {
    url: companyLocationIconUrl,
    anchor: new google.maps.Point(16, 16),
  };
}

function getCraftsmanMarker() {
  return {
    url: craftsmanLocationIconUrl,
    anchor: new google.maps.Point(16, 22),
    labelOrigin: new google.maps.Point(2, 2),
  };
}
